// ABOUTME: Modal for saving the current grid runtime state as a named config in userLayoutSlice.
// ABOUTME: Uses the SHModal pattern with name input + opt-in switches for filters, sort, and family.

import { useEffect, useMemo, useState } from "react";
import { Box, InputBase, useTheme } from "@mui/material";
import ArrowRightAltIcon from "@mui/icons-material/ArrowRightAlt";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import LinkOffIcon from "@mui/icons-material/LinkOff";
import { useTranslation } from "react-i18next";

import { tokens } from "../../../theme";
import SHModal from "../Modal/SHModal";
import Shbutton from "../buttons/Shbutton";
import ButtonGroup from "../buttons/ButtonGroup";
import Subheading from "../text/Subheading";
import Text from "../text/Text";
import ShSwitch from "../inputs/ShSwitch";
import { DropdownBaseV2 } from "../dropdowns/Dropdown";

/**
 * MSaveGridConfig
 *
 * Standard StockHub modal for saving the current grid state as a named view.
 * Uses the same SHModal + Shbutton footer pattern as MConfirmationWithValue.
 *
 * Props:
 *   open      — boolean
 *   handleClose — () => void
 *   onSave    — ({ name, includeFilters, includeSort, shareWith, setAsActive }) => void
 *                `shareWith` is an array of sibling gridKeys selected in the "share with"
 *                dropdown, or an empty array when the grid has no family / user unchecked all.
 *   defaultConfigId — string (the config that auto-loads on mount)
 *   familyMembers  — array of sibling gridKeys (excluding self). Empty/absent hides the dropdown.
 */
const MSaveGridConfig = (props) => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const { t } = useTranslation();

    const [name, setName] = useState("");
    const [includeFilters, setIncludeFilters] = useState(true);
    const [includeSort, setIncludeSort] = useState(true);
    const [shareWith, setShareWith] = useState([]);
    const [setAsActive, setSetAsActive] = useState(false);

    const cfg = props.activeConfig;
    const isAlreadyDefault = cfg && props.defaultConfigId === cfg.id;

    // Stable array of sibling members as { gridKey, label } objects (excluding self).
    const familyMembers = useMemo(
        () => (Array.isArray(props.familyMembers) ? props.familyMembers : []),
        [props.familyMembers]
    );
    const hasFamily = familyMembers.length > 0;

    // Just the gridKey strings — used for state and as the source-of-truth sibling list.
    const familyGridKeys = useMemo(
        () => familyMembers.map((m) => m.gridKey),
        [familyMembers]
    );

    // Dropdown options with translated labels. `id` is the gridKey so selected state
    // stays a list of gridKeys; `name` is the human-readable translation.
    const familyOptions = useMemo(
        () =>
            familyMembers.map((m) => ({
                id: m.gridKey,
                name: t(m.label ?? m.gridKey),
            })),
        [familyMembers, t]
    );

    // When the dialog opens, seed fields from the existing config or reset.
    useEffect(() => {
        if (!props.open) return;
        if (cfg) {
            setName(cfg.name ?? "");
            setIncludeFilters(cfg.filters != null);
            setIncludeSort(cfg.sortModel != null);
            setSetAsActive(isAlreadyDefault);
            // Preserve existing share list if the config was previously shared;
            // otherwise start empty — sharing is an explicit opt-in, not a default.
            setShareWith(
                Array.isArray(cfg.sharedWith) && cfg.sharedWith.length > 0
                    ? cfg.sharedWith.filter((k) => familyGridKeys.includes(k))
                    : []
            );
        } else {
            setName("");
            setIncludeFilters(true);
            setIncludeSort(true);
            setSetAsActive(false);
            setShareWith([]);
        }
    }, [props.open, cfg, isAlreadyDefault, familyGridKeys]);

    const isStandAlone = window.matchMedia("(display-mode: standalone)").matches;

    const handleSave = () => {
        if (!name.trim()) return;
        if (props.onSave) {
            props.onSave({
                name: name.trim(),
                includeFilters,
                includeSort,
                shareWith,
                setAsActive,
            });
        }
        setName("");
        setIncludeFilters(true);
        setIncludeSort(true);
        setShareWith([]);
        setSetAsActive(false);
        props.handleClose();
    };

    const handleKeyDown = (e) => {
        if (e.key === "Enter") handleSave();
    };

    const switchColor = theme.palette.mode === "dark" ? "blue" : "primary";

    return (
        <SHModal
            open={props.open}
            onClose={props.handleClose}
            onKeyDown={handleKeyDown}
        >
            <Box sx={{ maxWidth: 620 }}>
                <Box
                    sx={{
                        textAlign: "center",
                        paddingY: 3,
                        paddingX: 4,
                        display: "flex",
                        flexDirection: "column",
                    }}
                >
                    <Subheading>{t("save_view")}</Subheading>

                    {props.willUnlinkShared && (
                        <Box
                            sx={{
                                mt:1,
                                display: "flex",
                                alignItems: "center",
                                justifyContent:'space-between',
                                height:32,
                                borderRadius:5,
                                background: colors.orange[100],
                                border:`1px solid ${colors.orange[400]}`,
                                zIndex: 1,
                                px:2,
                                gap:1.5,
                            }}
                        >
                            <LinkOffIcon fontSize="small" sx={{ color: colors.orange[400] }} />
                            <Box sx={{ display: "flex", flexDirection: "column" }}>
                                <Text align="center" semibold variant="orange">
                                    {t("saving_will_unlink_shared_view")}
                                </Text>
                            </Box>
                            <Box sx={{flexShrink:0,width:32,}}></Box>
                        </Box>
                    )}
                </Box>

                <Box
                    sx={{
                        background:
                            theme.palette.mode === "dark" ? colors.grey[200] : colors.grey[0],
                        borderTopLeftRadius: 16,
                        borderTopRightRadius: 16,
                        px: 3,
                        pt: 3,
                        pb: 2,
                        display: "flex",
                        flexDirection: "column",
                        gap:1,
                    }}
                >
                    {/* Name field */}
                    <Box
                        sx={{
                            backgroundColor:
                                theme.palette.mode === "dark"
                                    ? colors.grey[300]
                                    : colors.grey[100],
                            borderRadius: 5,
                            display: "flex",
                            alignItems:'center',
                            height: 40,
                            px:0.5,
                        }}
                    >
                        <Box
                            sx={{
                                display: "flex",
                                alignItems:'center',
                                borderRadius:4,
                                height:32,
                                px:2,
                                background:theme.palette.mode === "dark" ? colors.grey[500] : colors.grey[0],
                                border: theme.palette.mode === "dark" ? null : `1px solid ${colors.grey[200]}`,
                                boxShadow:theme.palette.mode === "dark" ? null :  `0px 2px 4px ${colors.grey[200]}`,
                            }}
                        >
                            <Text semibold>{t("name")}</Text>
                        </Box>
                        <InputBase
                            autoFocus
                            sx={{
                                px: 2,
                                flex: 1,
                                color: colors.txt.primary,
                                lineHeight: "40px",
                                fontSize:14,
                            }}
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                        />
                        {hasFamily && (
                            <Box
                                sx={{
                                    height:32,
                                    borderRadius:4,
                                    background:colors.orange[100],
                                    border:`1px solid ${colors.orange[400]}`,
                                    display: "flex",
                                    pl:2,
                                    pr:0,
                                    alignItems: "center",
                                    gap: 1.5,
                                }}
                            >
                                <Text medium>{t("link_tabs")}</Text>
                                <DropdownBaseV2
                                    multiple
                                    options={familyOptions}
                                    selected={shareWith}
                                    valueKey="id"
                                    displayName="name"
                                    allowSelectAll
                                    onChange={(next) => setShareWith(next)}
                                >
                                    {({ onClick }) => (
                                        <Box
                                            onClick={onClick}
                                            sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                height: 32,
                                                px: 1,
                                                pl:2,
                                                gap:1,
                                                borderRadius: 4,
                                                cursor: "pointer",
                                                background:colors.orange[400],
                                                minWidth: 140,
                                                justifyContent: "space-between",
                                            }}
                                        >
                                            {shareWith.length === 0 ?
                                                <Text 
                                                    color={theme.palette.mode === "dark" ? colors.txt['primaryDark'] : colors.txt['light']} 
                                                    semibold
                                                >
                                                    {t("no_selection")}
                                                </Text>
                                                :
                                                shareWith.length === familyGridKeys.length ?
                                                <Text
                                                    color={theme.palette.mode === "dark" ? colors.txt['primaryDark'] : colors.txt['light']} 
                                                >
                                                    {t("all")}
                                                </Text>
                                                :
                                                <Box
                                                    sx={{
                                                        display: "flex",
                                                        alignItems: "center",
                                                        gap:1,
                                                    }}
                                                >
                                                    <Text 
                                                        color={theme.palette.mode === "dark" ? colors.txt['primaryDark'] : colors.txt['light']} 
                                                        semibold
                                                    >
                                                        {shareWith.length}
                                                    </Text>
                                                    <Text 
                                                        color={theme.palette.mode === "dark" ? colors.txt['primaryDark'] : colors.txt['light']} 
                                                    >
                                                        {t("selected")}
                                                    </Text>
                                                </Box>
                                            }
                                            {/* <Text semibold>
                                                {shareWith.length === 0
                                                    ? t("none")
                                                    : shareWith.length === familyGridKeys.length
                                                        ? t("all")
                                                        : `${shareWith.length}  ${t("selected")}`}
                                            </Text> */}
                                            <KeyboardArrowDownIcon 
                                                fontSize="small" 
                                                sx={{
                                                    color:theme.palette.mode === "dark" ? colors.txt['secondary'] : colors.txt['softened'],
                                                }} 
                                            />
                                        </Box>
                                    )}
                                </DropdownBaseV2>
                            </Box>
                        )}
                    </Box>

                    {/* Include filters */}
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                        }}
                    >
                        <Text medium>{t("include_filters")}</Text>
                        <ShSwitch
                            color={switchColor}
                            checked={includeFilters}
                            onChange={() => setIncludeFilters((v) => !v)}
                        />
                    </Box>

                    {/* Include sort */}
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                        }}
                    >
                        <Text medium>{t("include_sort")}</Text>
                        <ShSwitch
                            color={switchColor}
                            checked={includeSort}
                            onChange={() => setIncludeSort((v) => !v)}
                        />
                    </Box>

                    {/* Share with family members */}

                    {/* Set as active */}
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                        }}
                    >
                        <Text medium>{t("set_as_default")}</Text>
                        <ShSwitch
                            color={switchColor}
                            checked={setAsActive}
                            disabled={isAlreadyDefault}
                            onChange={() => setSetAsActive((v) => !v)}
                        />
                    </Box>
                </Box>

                {/* Footer */}
                <Box
                    component="footer"
                    sx={{
                        display: "flex",
                        width: 1,
                        paddingBottom: isStandAlone ? "32px" : "16px",
                        alignItems: "center",
                        paddingRight: 3,
                        paddingTop: 0,
                        background:
                            theme.palette.mode === "dark" ? colors.grey[200] : colors.grey[0],
                        borderBottomLeftRadius: 24,
                        borderBottomRightRadius: 24,
                        justifyContent: "space-between",
                        "@media screen and (max-width: 47.9375em)": {
                            paddingRight: 0,
                        },
                    }}
                >
                    <Shbutton
                        isCancel
                        className="always-visible"
                        onClick={props.handleClose}
                        color="primary"
                        variant="text"
                        startIcon={<ArrowRightAltIcon />}
                    >
                        {t("cancel")}
                    </Shbutton>
                    <ButtonGroup version={2}>
                        <Shbutton
                            className="group-btn"
                            color={theme.palette.mode === "dark" ? "blue" : "primary"}
                            onClick={handleSave}
                            variant="contained"
                            disabled={!name.trim()}
                        >
                            {t("save")}
                        </Shbutton>
                    </ButtonGroup>
                </Box>
            </Box>
        </SHModal>
    );
};

export default MSaveGridConfig;
